export function formatFieldLabel(field) {
    return field
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase())
        .trim();
}
export function isReferenceObject(value) {
    return (typeof value === 'object' &&
        value !== null &&
        'label' in value &&
        typeof value.label === 'string');
}
export function isReferenceArray(value) {
    return Array.isArray(value) && value.length > 0 && isReferenceObject(value[0]);
}
export function extractFieldValue(entity, field) {
    const rawValue = entity[field];
    if (rawValue === null || rawValue === undefined) {
        return undefined;
    }
    if (isReferenceArray(rawValue)) {
        return rawValue.map((r) => r.label).join(', ');
    }
    if (isReferenceObject(rawValue)) {
        return rawValue.label;
    }
    if (typeof rawValue === 'string') {
        return rawValue;
    }
    if (typeof rawValue === 'number') {
        return String(rawValue);
    }
    if (typeof rawValue === 'boolean') {
        return rawValue ? 'Yes' : 'No';
    }
    if (Array.isArray(rawValue)) {
        const stringItems = rawValue
            .map((item) => {
            if (typeof item === 'string')
                return item;
            if (isReferenceObject(item))
                return item.label;
            return null;
        })
            .filter(Boolean);
        return stringItems.length > 0 ? stringItems.join(', ') : undefined;
    }
    return undefined;
}
export function getFieldLabel(field, customLabels) {
    return customLabels[field] ?? formatFieldLabel(field);
}
export function hasFieldsWithValues(entity, section) {
    return section.fields.some((field) => {
        const value = extractFieldValue(entity, field);
        return value !== undefined && value !== null && value !== '';
    });
}
/**
 * Build badge configuration for the image gallery.
 * Entity-specific badge logic is centralized here.
 */
export function getEntityBadges(entity, entityType) {
    const badges = [];
    if (entityType === 'project') {
        const project = entity;
        const seasonLabel = project.season?.[0]?.label;
        if (seasonLabel) {
            badges.push({
                text: `Launch: ${seasonLabel}`,
                position: 'left',
                variant: 'pill'
            });
        }
        const creationTrackLabel = project.creationTrack?.label;
        if (creationTrackLabel) {
            badges.push({
                text: creationTrackLabel,
                position: 'right',
                variant: 'square'
            });
        }
    }
    return badges;
}
const ROLE_LABELS = {
    developer: 'Developer',
    plm: 'PLM',
    materialDeveloper: 'Material Developer',
    colorDesigner: 'Color Designer',
    graphicDesigner: 'Graphic Designer',
    productDesigner: 'Product Designer'
};
/**
 * Format upcharge value consistently across the application.
 * Handles undefined, null, and empty string values.
 *
 * @param upcharge - The upcharge value to format
 * @returns Formatted upcharge string or undefined
 */
export function formatUpcharge(upcharge) {
    if (upcharge === undefined || upcharge === null || upcharge === '') {
        return undefined;
    }
    const numericValue = Number(upcharge);
    if (Number.isNaN(numericValue)) {
        return undefined;
    }
    return `$${numericValue.toFixed(2)}`;
}
/**
 * Build team member list from Expression team object.
 * Extracts team members and formats role labels consistently.
 *
 * @param team - Expression team object
 * @returns Array of team member items for display
 */
export function buildExpressionTeamMembers(team) {
    if (!team)
        return [];
    const roleMappings = [
        { key: 'developer', field: 'developer' },
        { key: 'plm', field: 'plm' },
        { key: 'materialDeveloper', field: 'materialDeveloper' },
        { key: 'colorDesigner', field: 'colorDesigner' },
        { key: 'graphicDesigner', field: 'graphicDesigner' },
        { key: 'productDesigner', field: 'productDesigner' }
    ];
    return roleMappings.map(({ key, field }) => {
        const member = team[field];
        return {
            key,
            role: ROLE_LABELS[key],
            name: member?.name,
            email: member?.user
        };
    });
}
